import { CheckCircle2, Loader2, UploadCloud, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { uploadDocument } from "@/features/documents/api";
import { FolderSelect } from "@/features/documents/components/folder-select";
import type { DocumentFolder } from "@/features/documents/types";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

interface UploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  folders: DocumentFolder[];
  defaultFolderId: string | null;
  onUploaded: () => void;
}

type FileState = {
  file: File;
  status: "pending" | "uploading" | "done" | "error";
  message?: string;
};

/** Multi-file upload modal: pick files + target folder, then upload each with a
 * per-file result (ok / AV-rejected / type-rejected / too-large, from the server). */
export function UploadDialog({
  open,
  onOpenChange,
  clientId,
  folders,
  defaultFolderId,
  onUploaded,
}: UploadDialogProps) {
  const [folderId, setFolderId] = useState<string | null>(defaultFolderId);
  const [items, setItems] = useState<FileState[]>([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset the target folder each time the dialog opens on a new selection.
  useEffect(() => {
    if (open) {
      setFolderId(defaultFolderId);
      setItems([]);
    }
  }, [open, defaultFolderId]);

  function addFiles(fileList: FileList | null) {
    if (!fileList) return;
    const next = Array.from(fileList).map<FileState>((file) => ({
      file,
      status: "pending",
    }));
    setItems((prev) => [...prev, ...next]);
  }

  async function startUpload() {
    setBusy(true);
    let uploadedAny = false;
    for (let i = 0; i < items.length; i += 1) {
      const current = items[i];
      if (!current || current.status === "done") continue;
      setItems((prev) =>
        prev.map((it, idx) => (idx === i ? { ...it, status: "uploading" } : it)),
      );
      try {
        await uploadDocument(clientId, current.file, { folderId });
        uploadedAny = true;
        setItems((prev) =>
          prev.map((it, idx) => (idx === i ? { ...it, status: "done" } : it)),
        );
      } catch (err) {
        const message =
          err instanceof ApiError ? err.message : "Upload failed. Please try again.";
        setItems((prev) =>
          prev.map((it, idx) =>
            idx === i ? { ...it, status: "error", message } : it,
          ),
        );
      }
    }
    setBusy(false);
    if (uploadedAny) onUploaded();
  }

  const allDone = items.length > 0 && items.every((it) => it.status === "done");

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Document(s)</DialogTitle>
          <DialogDescription>
            Files are virus-scanned and encrypted at rest. Accepted: PDF, images,
            Office docs, CSV, TXT — up to 20&nbsp;MB each.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="upload-folder">Destination folder</Label>
            <FolderSelect
              id="upload-folder"
              folders={folders}
              value={folderId}
              onChange={setFolderId}
              rootLabel="Client root"
            />
          </div>

          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="border-border hover:bg-muted/40 flex flex-col items-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center"
          >
            <UploadCloud className="text-muted-foreground size-8" />
            <span className="text-sm font-medium">Click to choose files</span>
            <span className="text-muted-foreground text-xs">
              You can select multiple files
            </span>
          </button>
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            aria-label="Choose files to upload"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
          />

          {items.length > 0 && (
            <ul className="max-h-52 divide-y overflow-y-auto rounded-lg border">
              {items.map((it, idx) => (
                <li
                  key={`${it.file.name}-${idx}`}
                  className="flex items-center gap-3 px-3 py-2 text-sm"
                >
                  <FileStatusIcon status={it.status} />
                  <span className="flex-1 truncate" title={it.file.name}>
                    {it.file.name}
                  </span>
                  <span
                    className={cn(
                      "text-xs",
                      it.status === "error" ? "text-destructive" : "text-muted-foreground",
                    )}
                  >
                    {it.status === "error" ? it.message : `${Math.round(it.file.size / 1024)} KB`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {allDone ? "Close" : "Cancel"}
          </Button>
          <Button onClick={startUpload} disabled={busy || items.length === 0 || allDone}>
            {busy ? "Uploading…" : `Upload ${items.length || ""}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FileStatusIcon({ status }: { status: FileState["status"] }) {
  if (status === "done") return <CheckCircle2 className="text-success size-4 shrink-0" />;
  if (status === "error") return <XCircle className="text-destructive size-4 shrink-0" />;
  if (status === "uploading")
    return <Loader2 className="text-muted-foreground size-4 shrink-0 animate-spin" />;
  return <UploadCloud className="text-muted-foreground size-4 shrink-0" />;
}
